import { fetchJSON as fetchAPI } from './client';

export type PortfolioDashboard = {
  tenant_id: string;
  total_skus: number;
  total_collections: number;
  portfolio_growth_pct: number;
  innovation_index: number;
  lifecycle_balance_score: number;
  category_concentration_pct: number;
  active_recommendations: number;
  cannibalization_issues: number;
  innovation_pipeline_count: number;
  hero_product_count: number;
  declining_products: number;
};

export type PortfolioItem = {
  id: string;
  name: string;
  item_type: string;
  sku_count: number;
  revenue_pct: number;
  profit_pct: number;
  growth_rate_pct: number;
  lifecycle_stage?: string;
  health_score: number;
};

export type LifecycleRecord = {
  id: string;
  sku?: string;
  product_name: string;
  collection?: string;
  current_stage: string;
  recommended_action?: string;
  days_in_stage: number;
  revenue_trend?: string;
  next_stage?: string;
};

export type InnovationIdea = {
  id: string;
  title: string;
  description?: string;
  category?: string;
  evidence?: string;
  revenue_potential_pct: number;
  risk_score: number;
  status?: string;
  source_modules?: string[];
};

export type CannibalizationRecord = {
  id: string;
  product_a: string;
  product_b: string;
  conflict_type: string;
  overlap_score: number;
  revenue_impact_pct: number;
  recommendation?: string;
};

export type PortfolioScenario = {
  id: string;
  name: string;
  type: string;
  revenue_impact_pct: number;
  profit_impact_pct: number;
  skus_affected: number;
  risk_score: number;
  description?: string;
};

export type ResourceAllocation = {
  id: string;
  collection?: string;
  ad_budget_pct: number;
  content_investment_pct: number;
  inventory_investment_pct: number;
  innovation_budget_pct: number;
  ai_credits_pct: number;
  rationale?: string;
};

export type PortfolioRecommendation = {
  id: string;
  type: string;
  title: string;
  description?: string;
  evidence?: string;
  sku?: string;
  collection?: string;
  expected_impact?: string;
  confidence_score: number;
  priority_rank: number;
  requires_approval: boolean;
  lifecycle_stage?: string;
};

const API_BASE = import.meta.env.VITE_BRANDPORTFOLIO_API ?? '/api/v1/brandportfolio';

function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const brandportfolioApi = {
  dashboard: () => fetchJSON<PortfolioDashboard>('/dashboard'),
  listPortfolio: () => fetchJSON<PortfolioItem[]>('/portfolio'),
  listLifecycle: () => fetchJSON<LifecycleRecord[]>('/lifecycle'),
  listInnovation: () => fetchJSON<InnovationIdea[]>('/innovation'),
  listCannibalization: () => fetchJSON<CannibalizationRecord[]>('/cannibalization'),
  listScenarios: () => fetchJSON<PortfolioScenario[]>('/scenarios'),
  simulateScenario: (type: string) =>
    fetchJSON<PortfolioScenario>('/scenarios/simulate', { method: 'POST', body: JSON.stringify({ type }) }),
  listAllocation: () => fetchJSON<ResourceAllocation[]>('/allocation'),
  listRecommendations: () => fetchJSON<PortfolioRecommendation[]>('/recommendations'),
  generateRecommendations: () =>
    fetchJSON<PortfolioRecommendation[]>('/recommendations/generate', { method: 'POST' }),
  approveRecommendation: (id: string) =>
    fetchJSON<PortfolioRecommendation>(`/recommendations/${id}/approve`, { method: 'POST', body: JSON.stringify({}) }),
  listActivity: () => fetchJSON<{ id: string; message: string }[]>('/activity'),
};
